import { For, Show } from 'solid-js'
import { DailyBox } from '~/components/daily-box'
import { ClockIcon } from '~/components/icons/clock'
import { createCountdown, splitSeconds } from '~/lib/countdown'
import { m } from '~/paraglide/messages'
import type { CheckinSlot } from '~/resources/focus-challenges'

const SKELETON_SLOTS = [0, 1, 2] as const

type Props = {
  slots: CheckinSlot[]
  resetSeconds: number
  loading?: boolean
  claimingId?: number | null
  onPressStart: (slotIndex: number) => void
  onPressEnd: (slotIndex: number) => void
  onClickBox: (
    slotIndex: number,
    el: HTMLButtonElement,
    event: MouseEvent,
  ) => void
}

function WaitingBox(props: {
  slot: CheckinSlot
  index: number
  onPressStart: (i: number) => void
  onPressEnd: (i: number) => void
  onClickBox: (i: number, el: HTMLButtonElement, e: MouseEvent) => void
}) {
  const parts = createCountdown(() => props.slot.progress?.available_at ?? null)
  return (
    <DailyBox
      state={props.slot.state}
      countdownSeconds={parts().totalSeconds}
      onPressStart={() => props.onPressStart(props.index)}
      onPressEnd={() => props.onPressEnd(props.index)}
      onClick={(el, e) => props.onClickBox(props.index, el, e)}
    />
  )
}

export function DailyCheckinCard(props: Props) {
  return (
    <section class="-mt-4 w-full">
      <div class="mx-auto flex w-full max-w-[640px] flex-col gap-[16px] overflow-hidden rounded-[20px] bg-white p-[20px] shadow-[0_4px_20px_-10px_rgba(0,0,0,0.08)]">
        <header class="flex w-full items-start justify-between gap-2">
          <h2 class="min-w-[180px] max-w-[60%] text-[18px] font-semibold leading-[26px] text-ink">
            {m.checkintask_title()}
          </h2>
          <span class="flex min-w-0 flex-1 items-start justify-end gap-1 text-xs text-[#888]">
            <ClockIcon class="mt-[1px] size-[14px] shrink-0" />
            <span class="text-right">
              {m.daily_challenge_timer(splitSeconds(props.resetSeconds))}
            </span>
          </span>
        </header>

        <div class="flex w-full items-center justify-center gap-[12px]">
          <Show
            when={!props.loading}
            fallback={
              <For each={SKELETON_SLOTS}>
                {() => (
                  <DailyBox
                    state="pending"
                    loading
                    onPressStart={() => {}}
                    onPressEnd={() => {}}
                    onClick={() => {}}
                  />
                )}
              </For>
            }
          >
            <For each={props.slots}>
              {(slot, index) => (
                <Show
                  when={slot.state === 'waiting'}
                  fallback={
                    <DailyBox
                      state={slot.state}
                      claiming={
                        props.claimingId != null &&
                        slot.progress?.id === props.claimingId
                      }
                      onPressStart={() => props.onPressStart(index())}
                      onPressEnd={() => props.onPressEnd(index())}
                      onClick={(el, e) => props.onClickBox(index(), el, e)}
                    />
                  }
                >
                  <WaitingBox
                    slot={slot}
                    index={index()}
                    onPressStart={props.onPressStart}
                    onPressEnd={props.onPressEnd}
                    onClickBox={props.onClickBox}
                  />
                </Show>
              )}
            </For>
          </Show>
        </div>
      </div>
    </section>
  )
}
